/**
 * Per-clinic reminder message templates.
 *
 * Storage: `clinics.settings.reminder_templates` JSONB. No schema column
 * — keeps adding new reminder types cheap.
 *
 * Render: simple {{variable}} substitution. No conditionals or loops.
 * Unknown variables render as empty string (silent fallback).
 *
 * Fallback: if a template is missing or an empty string, the cron
 * uses the hardcoded default from this file. Owners can always reset
 * to default by clearing the field.
 */
import { z } from "zod";

// ===== Reminder types =====

export const REMINDER_TYPES = ["reminder_24h"] as const;
export const reminderTypeSchema = z.enum(REMINDER_TYPES);
export type ReminderType = z.infer<typeof reminderTypeSchema>;

// ===== Template languages =====
// Mirrors the patient preferred_language enum — the reminder goes out in the
// PATIENT's language (ckb Kurdish wording sourced from the app's own vetted
// translations, not machine-translated).
export const REMINDER_LANGS = ["ar", "en", "ckb"] as const;
export type ReminderLang = (typeof REMINDER_LANGS)[number];

// ===== Template variables =====
// Names match what the renderer substitutes — keep this list in sync
// with the cron when adding new vars.
export const REMINDER_VARIABLES = [
	"patient_name",
	"clinic_name",
	"appointment_time",
	"appointment_date",
	"doctor_name",
	"service_name",
	"confirm_url",
	"cancel_url",
	"reschedule_url",
] as const;
export type ReminderVariable = (typeof REMINDER_VARIABLES)[number];

// ===== Defaults =====
// EXACT match to the strings the reminders cron used to hardcode. Any
// edit here changes the behavior for clinics that haven't customized
// their template — keep the wording stable.
export const DEFAULT_REMINDER_TEMPLATES: Record<
	ReminderType,
	Record<ReminderLang, string>
> = {
	reminder_24h: {
		ar:
			"مرحباً {{patient_name}}،\n" +
			"تذكير بموعدك في {{clinic_name}} يوم {{appointment_time}}.\n\n" +
			"✅ تأكيد الحضور: {{confirm_url}}\n" +
			"📅 تغيير الموعد: {{reschedule_url}}\n" +
			"❌ إلغاء الموعد: {{cancel_url}}",
		en:
			"Hi {{patient_name}},\n" +
			"Reminder: your appointment at {{clinic_name}} on {{appointment_time}}.\n\n" +
			"✅ Confirm: {{confirm_url}}\n" +
			"📅 Reschedule: {{reschedule_url}}\n" +
			"❌ Cancel: {{cancel_url}}",
		// Composed from the app's vetted ckb strings (appointments:whatsapp.* +
		// appointments:detail.cancel_title + public wa greeting).
		ckb:
			"سڵاو {{patient_name}}،\n" +
			"بیرخستنەوە: ژووانەکەت لە {{clinic_name}} لە {{appointment_time}}.\n\n" +
			"✅ پشتڕاستکردنەوەی ئامادەبوون: {{confirm_url}}\n" +
			"📅 گۆڕینی ژووان: {{reschedule_url}}\n" +
			"❌ هەڵوەشاندنەوەی ژووان: {{cancel_url}}",
	},
};

// ===== API shape =====

export const reminderTemplateBodySchema = z.object({
	ar: z.string().max(2000),
	en: z.string().max(2000),
	ckb: z.string().max(2000),
});
export type ReminderTemplateBody = z.infer<typeof reminderTemplateBodySchema>;

/**
 * What `clinics.settings.reminder_templates` looks like. All keys are
 * optional — an absent or empty string falls back to DEFAULT_REMINDER_TEMPLATES.
 */
export const reminderTemplatesSchema = z.object({
	reminder_24h: reminderTemplateBodySchema.partial().optional(),
});
export type ReminderTemplatesStored = z.infer<typeof reminderTemplatesSchema>;

/**
 * GET /settings/reminder-templates returns the *effective* templates —
 * customized values merged on top of defaults — so the editor can
 * show "what the patient actually receives" without re-implementing
 * the fallback in the UI.
 */
export interface ReminderTemplatesResponse {
	templates: Record<ReminderType, Record<ReminderLang, string>>;
	/**
	 * Which fields the clinic has actually customized (vs left as default).
	 * UI uses this to show "Reset" buttons only on customized fields.
	 */
	customized: Record<ReminderType, Record<ReminderLang, boolean>>;
	/** The defaults — so the editor can preview them without an extra call. */
	defaults: Record<ReminderType, Record<ReminderLang, string>>;
}

export const updateReminderTemplatesInputSchema = z.object({
	reminder_24h: reminderTemplateBodySchema.partial().optional(),
});
export type UpdateReminderTemplatesInput = z.infer<
	typeof updateReminderTemplatesInputSchema
>;

// ===== Reminder send queue (manual WhatsApp flow) =====

/** One upcoming appointment the staff may send a reminder for. */
export interface ReminderQueueItem {
	appointment_id: string;
	patient_name: string;
	patient_phone: string | null;
	scheduled_at: string; // ISO
	title: string | null;
	/**
	 * Appointment status — the queue only lists booked/confirmed. 'confirmed'
	 * means the patient already confirmed (via the reminder link), so reception
	 * can skip re-sending a confirm reminder.
	 */
	status: "booked" | "confirmed";
	/** True once the staff marked the manual reminder as sent. */
	sent: boolean;
	manual_sent_at: string | null;
	/** True if the 24h auto-reminder notification was already generated. */
	auto_notified: boolean;
}

/** Result of preparing a reminder for one appointment. */
export interface PrepareReminderResult {
	channel: "manual" | "cloud_api";
	/** True when the server already delivered it (cloud_api). */
	sent: boolean;
	/** For `manual`: the wa.me link to open. Null for cloud_api. */
	whatsapp_url: string | null;
	message: string;
}

// ===== Render =====

/**
 * Replace every `{{var}}` occurrence in template with vars[var]. Unknown
 * vars become empty strings — keeps the message coherent even when the
 * appointment is missing a doctor or service.
 *
 * Whitespace inside `{{ var }}` is tolerated.
 */
export function renderReminderTemplate(
	template: string,
	vars: Partial<Record<ReminderVariable, string>>,
): string {
	return template.replace(/\{\{\s*(\w+)\s*\}\}/gu, (_, key: string) => {
		const v = (vars as Record<string, string | undefined>)[key];
		return v ?? "";
	});
}

/**
 * Resolve the effective template for a (type, lang) tuple — clinic
 * override if present and non-empty, otherwise the default.
 */
export function resolveReminderTemplate(
	stored: ReminderTemplatesStored | null | undefined,
	type: ReminderType,
	lang: ReminderLang,
): string {
	const custom = stored?.[type]?.[lang];
	if (custom && custom.trim().length > 0) return custom;
	return DEFAULT_REMINDER_TEMPLATES[type][lang];
}

/** Map a patient's preferred_language value onto a template language. */
export function reminderLangFor(
	patientLang: string | null | undefined,
): ReminderLang {
	return patientLang === "en" ? "en" : patientLang === "ckb" ? "ckb" : "ar";
}
